import type { SampleReport } from '@ctcells/contract'
import { enumLabel, maskPii, t } from '@ctcells/i18n'

import { EmptyState } from '../../atoms/EmptyState.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { dateTimeText, orDash } from '../format.ts'

/**
 * 시료 보고서 — `GET /reports/{sample_id}`.
 *
 * 🔴 **가장 작은 형태로 둔다**(대화 하나). 새 라우트를 만들지 않았다.
 * 🔴 **시료 서랍 · 모니터링 서랍이 같은 대화를 연다**(2026-09-28 여기로 옮겼다 — 종전에는
 *    `BatchDetailScreen` 안에 있었고 시료 서랍의 「보고서」 버튼은 핸들러 없는 죽은 버튼이었다).
 * ⚠ 경로 파라미터는 **`sample_id`** 다(`app/views/reports.py` — 보고서 ID 가 따로 없다). 서버는
 *   **존재하는 시료면 누구든** 보고서를 만든다(진행 중 · 미배정 · 폐기 포함 — 2026-09-28 실측 200).
 *   없는 시료만 404 다. viewer 는 403(`REPORT_ROLES = {operator, admin}`).
 * ⚠ `events[]` 는 그리지 않는다 — 이 대화는 요약 칸만 적는다.
 */
export interface SampleReportModalProps {
  sampleId: string
  report: SampleReport | undefined
  loading: boolean
  error: string | undefined
  /** `view` 권한 — 식별자 마스킹 */
  view: boolean
  onClose: () => void
}

/**
 * 분주가 **0 건일 때** 무엇이라 적을까 — 🔴 **「없음」은 «단언»이다.**
 *
 * 보고서는 ALCOA+ 산출물이라 「분주가 없었다」와 「기록을 못 봤다」를 같은 `—` 로 적으면
 * **그 보고서가 거짓을 말한다.** 서버가 그 갈림을 `SampleReport.dispenses_basis` 로 준다
 * (ctcells-core 2026-09-21 `f2269c1`). 실측 `GET /reports/FE-0921-H1-04` → `"unobserved"`.
 *
 * 🔴 **칸이 없으면 「없음」으로 접지 않는다** — 판정을 «안 낸» 서버판이라는 뜻이므로
 *    종전 문구(`—`)를 그대로 쓴다. 없는 판정을 우리가 지어내지 않는다.
 * ⚠ `reagent_lots_used` 도 같은 문구를 쓴다 — **같은 행에서 파생**되므로 근거가 같다.
 */
function emptyDispenseText(basis: SampleReport['dispenses_basis']): string {
  if (basis === undefined) return t('mask.none')
  return t(`enum.dispensesBasis.${basis}`)
}

export function SampleReportModal({
  sampleId,
  report,
  loading,
  error,
  view,
  onClose,
}: SampleReportModalProps) {
  return (
    <Modal size="sm" onClose={onClose} title={t('screen.sampleReport.title')}>
      {loading ? (
        /* 무엇을 여는 중인지 보인다 — 빈 대화가 뜨면 사람이 잘못 눌렀나 의심한다 */
        <EmptyState label={`${maskPii(sampleId, view)} · ${t('state.loading')}`} />
      ) : error !== undefined ? (
        <p className="vissue" role="alert">
          {error}
        </p>
      ) : report === undefined ? (
        <EmptyState label={t('state.empty.reports')} />
      ) : (
        <ValueRows
          rows={[
            {
              key: 'sample',
              label: t('column.sampleId'),
              value: maskPii(report.sample_id, view),
            },
            { key: 'batch', label: t('column.batch'), value: orDash(report.batch_id) },
            {
              key: 'stage',
              label: t('column.step'),
              value: enumLabel('enum.sampleStage', report.stage),
            },
            {
              key: 'status',
              label: t('column.status'),
              value: enumLabel('enum.sampleStatus', report.status),
            },
            ...(report.discard_reason == null
              ? []
              : [
                  {
                    key: 'discard',
                    label: t('column.reasonChange'),
                    value: report.discard_reason,
                  },
                ]),
            {
              key: 'recipe',
              label: t('column.recipe'),
              /* 🔴 미배정 시료는 둘 다 null 이다(배치 바깥 조인) — 「— · vnull」을 찍지 않는다 */
              value:
                report.recipe_id == null
                  ? t('mask.none')
                  : report.recipe_version == null
                    ? report.recipe_id
                    : `${report.recipe_id} · v${String(report.recipe_version)}`,
            },
            {
              key: 'completed',
              label: t('column.completedAt'),
              value: dateTimeText(report.completed_at),
            },
            {
              key: 'lots',
              label: t('row.reagentLots'),
              value:
                report.reagent_lots_used.length === 0
                  ? emptyDispenseText(report.dispenses_basis)
                  : report.reagent_lots_used.join(' · '),
            },
            {
              key: 'dispenses',
              label: t('row.dispenseCount'),
              value:
                report.dispenses.length === 0
                  ? emptyDispenseText(report.dispenses_basis)
                  : String(report.dispenses.length),
            },
          ]}
        />
      )}
    </Modal>
  )
}
